import { canReturnToDesign, getEligibleEvents } from '@neopolis/engine';
import type { Content, GameEvent, GameState } from '@neopolis/engine';

import { T } from '../../textes-interface.js';
import { MessageDuMaire } from '../MessageDuMaire.js';

/**
 * Étape 4 — Améliorer.
 *
 * Les événements éligibles se présentent un par un. Certains ouvrent un débat
 * de classe, que l'interface met à part : ce n'est pas une question à laquelle
 * l'îlot répond sur sa tablette, c'est une question pour tout le monde.
 *
 * L'îlot peut revenir une fois sur sa conception. La ville reprend alors son
 * état d'avant, et les événements sont rejoués sur la nouvelle solution.
 */
export function EtapeAmeliorer({
  content,
  partie,
  messages,
  debat,
  onResoudre,
  onRevenirConcevoir,
}: {
  content: Content;
  partie: GameState;
  messages: readonly string[];
  debat: string | null;
  onResoudre: (eventId: string, choiceId: string | null) => void;
  onRevenirConcevoir: () => void;
}) {
  const eligibles = getEligibleEvents(partie, content, partie.chapterId);
  const evenement: GameEvent | undefined = eligibles[0];
  const peutRevenir = canReturnToDesign(partie, content);

  return (
    <div className="space-y-4">
      {evenement ? (
        <MessageDuMaire titre={evenement.titre}>
          <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
            {T.ameliorer.evenement}
          </p>
          <p>{evenement.texte}</p>

          {evenement.choix ? (
            <div className="mt-3 space-y-2">
              {evenement.choix.map((choix) => (
                <button
                  key={choix.id}
                  type="button"
                  onClick={() => onResoudre(evenement.id, choix.id)}
                  className="min-h-touch w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-2 text-left font-medium text-slate-800 hover:bg-sky-50"
                >
                  {choix.texte}
                </button>
              ))}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => onResoudre(evenement.id, null)}
              className="mt-3 min-h-touch rounded-xl bg-sky-600 px-6 font-semibold text-white hover:bg-sky-700"
            >
              {T.maire.continuer}
            </button>
          )}
        </MessageDuMaire>
      ) : (
        <p className="rounded-2xl bg-emerald-50 p-4 font-semibold text-emerald-900">
          {T.ameliorer.termine}
        </p>
      )}

      {messages.length > 0 && (
        <section aria-live="polite" className="rounded-2xl border-2 border-slate-200 bg-white p-4">
          <ul className="space-y-2">
            {messages.map((message) => (
              <li key={message} className="text-slate-800">
                {message}
              </li>
            ))}
          </ul>
        </section>
      )}

      {debat && (
        <section className="rounded-2xl border-2 border-violet-300 bg-violet-50 p-4">
          <h3 className="text-sm font-bold uppercase tracking-wide text-violet-800">
            <span aria-hidden="true">🗣️ </span>
            {T.ameliorer.debat}
          </h3>
          <p className="mt-1 text-lg text-slate-900">{debat}</p>
        </section>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-4">
        <p className="text-sm text-slate-600">
          {peutRevenir ? T.ameliorer.revenirAide : T.ameliorer.plusDeRetour}
        </p>
        <button
          type="button"
          disabled={!peutRevenir}
          onClick={onRevenirConcevoir}
          className="mt-2 min-h-touch w-full rounded-xl bg-white px-6 font-semibold text-sky-700 ring-2 ring-sky-600 hover:bg-sky-50 disabled:text-slate-400 disabled:ring-slate-300"
        >
          {T.ameliorer.revenirConcevoir}
        </button>
      </section>
    </div>
  );
}
